import { z } from "zod";
import { cursorQuerySchema, timestampSchema, uuidSchema } from "./api.ts";
import { actorSchema, anyEntityTypeSchema, csvArraySchema, timestampsShape, userRefSchema, type EntityType } from "./common.ts";
import type { Permission } from "./permissions.ts";

/**
 * ACTIVITIES, FOLLOW-UPS AND MESSAGE TEMPLATES — the human interaction timeline
 * (PRD §4.3 and §7 Item 6), deliberately distinct from the audit trail.
 *
 * The audit log records what the SYSTEM did to a record; an activity records
 * what a PERSON did with a human being — a call, a meeting, a note, an email, a
 * WhatsApp message. Both appear on one unified timeline, visually distinct.
 *
 * An activity is linked to MANY records (lead, client, company, contact,
 * project) through `links`, which is what lets a lead's history follow the
 * client at conversion by RE-LINKING rather than copying (PRD C-6: "none is
 * duplicated in storage").
 */

// ---------------------------------------------------------------------------
// Enumerations
// ---------------------------------------------------------------------------

/** `system_milestone` is written by other modules through `logActivity`, never by a user. */
export const ACTIVITY_TYPES = ["call", "meeting", "note", "email", "whatsapp", "form_submission", "system_milestone"] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];
export const activityTypeSchema = z.enum(ACTIVITY_TYPES);

/** The types a user may log by hand (US-6.1). */
export const LOGGABLE_ACTIVITY_TYPES = ["call", "meeting", "note", "email", "whatsapp"] as const satisfies readonly ActivityType[];
export type LoggableActivityType = (typeof LOGGABLE_ACTIVITY_TYPES)[number];
export const loggableActivityTypeSchema = z.enum(LOGGABLE_ACTIVITY_TYPES);

export const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  call: "Call",
  meeting: "Meeting",
  note: "Note",
  email: "Email",
  whatsapp: "WhatsApp",
  form_submission: "Form submission",
  system_milestone: "Milestone",
};

export const ACTIVITY_DIRECTIONS = ["inbound", "outbound"] as const;
export type ActivityDirection = (typeof ACTIVITY_DIRECTIONS)[number];
export const activityDirectionSchema = z.enum(ACTIVITY_DIRECTIONS);

/** Call outcomes (US-6.1). `rescheduled` covers "reached, moved to another slot". */
export const CALL_OUTCOMES = ["connected", "no_answer", "busy", "wrong_number", "rescheduled"] as const;
export type CallOutcome = (typeof CALL_OUTCOMES)[number];
export const callOutcomeSchema = z.enum(CALL_OUTCOMES);

export const CALL_OUTCOME_LABELS: Record<CallOutcome, string> = {
  connected: "Connected",
  no_answer: "No answer",
  busy: "Busy",
  wrong_number: "Wrong number",
  rescheduled: "Rescheduled",
};

/**
 * US-6.7. Enforced on the SERVER in the timeline, in lists, in search and in
 * exports — never in the UI alone:
 *   · `everyone`             — any member who can read the linked record
 *   · `sales_and_management` — hidden from delivery-only roles
 *   · `private`              — the author (or the person who made the call) and
 *                              the workspace owner, nobody else
 */
export const ACTIVITY_VISIBILITIES = ["everyone", "sales_and_management", "private"] as const;
export type ActivityVisibility = (typeof ACTIVITY_VISIBILITIES)[number];
export const activityVisibilitySchema = z.enum(ACTIVITY_VISIBILITIES);

export const ACTIVITY_VISIBILITY_LABELS: Record<ActivityVisibility, string> = {
  everyone: "Everyone",
  sales_and_management: "Sales & management",
  private: "Private",
};

/**
 * Visibility is expressed as PERMISSIONS rather than role keys, so a custom role
 * behaves predictably and the same rule can be written in SQL, in the search
 * index and in the web app.
 *
 * `leads:write` is held by Owner, Admin and Manager and by nobody in a
 * delivery-only or finance-only role — that is exactly "sales & management".
 * `billing:manage` is held by the Owner alone among the system roles, which is
 * how "the author and the workspace owner" is enforced on a private note.
 */
export const SALES_VISIBILITY_PERMISSION = "leads:write" satisfies Permission;
export const PRIVATE_VISIBILITY_BYPASS_PERMISSION = "billing:manage" satisfies Permission;

/** Server-side and client-side answer to "may this caller see a sales-only entry?". */
export function canSeeSalesVisibility(permissions: ReadonlySet<string> | readonly string[]): boolean {
  return Array.isArray(permissions) ? permissions.includes(SALES_VISIBILITY_PERMISSION) : (permissions as ReadonlySet<string>).has(SALES_VISIBILITY_PERMISSION);
}

/** Record types an activity or a follow-up can hang off. */
export const ACTIVITY_LINK_TYPES = ["lead", "client", "company", "contact", "project", "task"] as const satisfies readonly EntityType[];
export type ActivityLinkType = (typeof ACTIVITY_LINK_TYPES)[number];
export const activityLinkTypeSchema = z.enum(ACTIVITY_LINK_TYPES);

export const FOLLOW_UP_STATUSES = ["open", "done", "cancelled"] as const;
export type FollowUpStatus = (typeof FOLLOW_UP_STATUSES)[number];
export const followUpStatusSchema = z.enum(FOLLOW_UP_STATUSES);

/** "My follow-ups" buckets (US-6.2). `done` covers completed and cancelled. */
export const FOLLOW_UP_BUCKETS = ["overdue", "today", "upcoming", "done"] as const;
export type FollowUpBucket = (typeof FOLLOW_UP_BUCKETS)[number];
export const followUpBucketSchema = z.enum(FOLLOW_UP_BUCKETS);

export const MESSAGE_TEMPLATE_CHANNELS = ["email", "whatsapp"] as const;
export type MessageTemplateChannel = (typeof MESSAGE_TEMPLATE_CHANNELS)[number];
export const messageTemplateChannelSchema = z.enum(MESSAGE_TEMPLATE_CHANNELS);

// ---------------------------------------------------------------------------
// Merge fields (templates, click-to-WhatsApp)
// ---------------------------------------------------------------------------

/**
 * Tokens a template body may contain, written `{{contact.first_name}}`.
 * The server resolves what it can from the activity's links and reports the
 * rest in `missingMergeFields`, so the user sees the gap before sending.
 */
export const MERGE_FIELDS = [
  { key: "contact.first_name", label: "Contact first name", sample: "Priya" },
  { key: "contact.name", label: "Contact full name", sample: "Priya Sharma" },
  { key: "contact.email", label: "Contact email", sample: "priya@acme.in" },
  { key: "contact.phone", label: "Contact phone", sample: "+919812345678" },
  { key: "company.name", label: "Company name", sample: "Acme Retail" },
  { key: "client.name", label: "Client name", sample: "Acme Retail" },
  { key: "client.number", label: "Client number", sample: "C-0042" },
  { key: "lead.number", label: "Lead number", sample: "L-000123" },
  { key: "user.name", label: "Your name", sample: "Rahul" },
  { key: "org.name", label: "Workspace name", sample: "Lean Agency" },
  { key: "today", label: "Today's date", sample: "18/09/2026" },
] as const;
export type MergeFieldKey = (typeof MERGE_FIELDS)[number]["key"];
export const MERGE_FIELD_KEYS = MERGE_FIELDS.map((f) => f.key) as readonly MergeFieldKey[];

const MERGE_TOKEN_REGEX = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;

/** Every token used in a template body/subject, in order of first appearance. */
export function mergeFieldsUsed(template: string): string[] {
  const out: string[] = [];
  for (const match of template.matchAll(MERGE_TOKEN_REGEX)) {
    const key = match[1];
    if (key && !out.includes(key)) out.push(key);
  }
  return out;
}

/**
 * Pure renderer shared by the API and the web preview. An unresolved token is
 * replaced with an empty string (never left as `{{…}}` in a message somebody is
 * about to send) and reported in `missing`.
 */
export function renderMergeTemplate(template: string, values: Readonly<Record<string, string | null | undefined>>): { text: string; missing: string[] } {
  const missing: string[] = [];
  const text = template.replace(MERGE_TOKEN_REGEX, (_full, rawKey: string) => {
    const key = rawKey.trim();
    const value = values[key];
    if (value === undefined || value === null || value === "") {
      if (!missing.includes(key)) missing.push(key);
      return "";
    }
    return value;
  });
  return { text, missing };
}

// ---------------------------------------------------------------------------
// Click-to-chat / click-to-call (PRD §5.5 Phase 1 — no Business API yet)
// ---------------------------------------------------------------------------

export const DEFAULT_PHONE_COUNTRY_CODE = "91";

/**
 * wa.me wants digits only, with the country code and no "+". An Indian 10-digit
 * number typed without a prefix gets one, because that is what every agency in
 * the target market stores. Returns null when nothing usable is left.
 */
export function normalizeWhatsappNumber(phone: string, defaultCountryCode: string = DEFAULT_PHONE_COUNTRY_CODE): string | null {
  const digits = phone.replace(/\D/g, "").replace(/^0+/, "");
  if (digits.length < 6 || digits.length > 15) return digits.length === 10 ? `${defaultCountryCode}${digits}` : null;
  if (digits.length === 10) return `${defaultCountryCode}${digits}`;
  return digits;
}

/** `https://wa.me/<digits>?text=<url-encoded message>` (US-6.4). */
export function waMeUrl(phone: string, text?: string | null, defaultCountryCode: string = DEFAULT_PHONE_COUNTRY_CODE): string | null {
  const number = normalizeWhatsappNumber(phone, defaultCountryCode);
  if (!number) return null;
  return text ? `https://wa.me/${number}?text=${encodeURIComponent(text)}` : `https://wa.me/${number}`;
}

/** `tel:` link for the mobile UI. */
export function telLink(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, "")}`;
}

// ---------------------------------------------------------------------------
// Activity DTOs
// ---------------------------------------------------------------------------

/** A person on an activity: a contact at the client, or a member of the workspace. */
export const activityParticipantSchema = z.object({
  kind: z.enum(["contact", "member"]),
  id: uuidSchema,
  name: z.string(),
  avatarUrl: z.string().nullable(),
});
export type ActivityParticipant = z.infer<typeof activityParticipantSchema>;

/** One edge of the many-to-many. `isPreSale` is set when the link came from a lead conversion. */
export const activityLinkSchema = z.object({
  entityType: activityLinkTypeSchema,
  entityId: uuidSchema,
  isPreSale: z.boolean(),
});
export type ActivityLink = z.infer<typeof activityLinkSchema>;

export const activityLinkRefSchema = z.object({ entityType: activityLinkTypeSchema, entityId: uuidSchema });
export type ActivityLinkRef = z.infer<typeof activityLinkRefSchema>;

export const activitySchema = z.object({
  id: uuidSchema,
  type: activityTypeSchema,
  occurredAt: timestampSchema,
  direction: activityDirectionSchema.nullable(),
  subject: z.string().nullable(),
  body: z.string().nullable(),
  /** Calls only. */
  outcome: callOutcomeSchema.nullable(),
  durationMinutes: z.number().int().nullable(),
  visibility: activityVisibilitySchema,
  isPinned: z.boolean(),
  /** The member who had the interaction (null for system / automation entries). */
  actor: userRefSchema.nullable(),
  /** The member who logged it — usually, but not always, the same person. */
  createdBy: userRefSchema.nullable(),
  participants: z.array(activityParticipantSchema),
  links: z.array(activityLinkSchema),
  attachmentFileIds: z.array(uuidSchema),
  metadata: z.record(z.string(), z.unknown()),
  customFields: z.record(z.string(), z.unknown()),
  ...timestampsShape,
});
export type ActivityDto = z.infer<typeof activitySchema>;

// ---------------------------------------------------------------------------
// Activity inputs
// ---------------------------------------------------------------------------

/** Shape WITHOUT defaults, so `.partial()` below cannot resurrect them (zod 4). */
const activityWritableShape = {
  type: loggableActivityTypeSchema,
  occurredAt: timestampSchema,
  direction: activityDirectionSchema.nullable(),
  subject: z.string().trim().max(300).nullable(),
  body: z.string().trim().max(20_000).nullable(),
  outcome: callOutcomeSchema.nullable(),
  durationMinutes: z.number().int().min(0).max(24 * 60).nullable(),
  visibility: activityVisibilitySchema,
  actorUserId: uuidSchema.nullable(),
  participantContactIds: z.array(uuidSchema).max(50),
  participantMemberIds: z.array(uuidSchema).max(50),
  attachmentFileIds: z.array(uuidSchema).max(20),
  metadata: z.record(z.string(), z.unknown()),
  customFields: z.record(z.string(), z.unknown()),
};

/** The inline "next step" of US-6.1 — one activity and its follow-up in a single call. */
export const inlineFollowUpInputSchema = z
  .object({
    title: z.string().trim().min(1).max(300),
    dueAt: timestampSchema,
    reminderAt: timestampSchema.nullable().optional(),
    assignedToUserId: uuidSchema.optional(),
    note: z.string().trim().max(2000).nullable().optional(),
  })
  .strict();
export type InlineFollowUpInput = z.input<typeof inlineFollowUpInputSchema>;

/**
 * POST /api/v1/activities (perm `messages:write`) → 201 Activity.
 * `links` must name at least one record — an activity nobody can find is not
 * worth storing. Unreadable or cross-workspace link targets → 404.
 */
export const createActivityInputSchema = z
  .object({
    ...activityWritableShape,
    occurredAt: activityWritableShape.occurredAt.optional(),
    direction: activityWritableShape.direction.optional(),
    subject: activityWritableShape.subject.optional(),
    body: activityWritableShape.body.optional(),
    outcome: activityWritableShape.outcome.optional(),
    durationMinutes: activityWritableShape.durationMinutes.optional(),
    visibility: activityWritableShape.visibility.optional(),
    actorUserId: activityWritableShape.actorUserId.optional(),
    participantContactIds: activityWritableShape.participantContactIds.optional(),
    participantMemberIds: activityWritableShape.participantMemberIds.optional(),
    attachmentFileIds: activityWritableShape.attachmentFileIds.optional(),
    metadata: activityWritableShape.metadata.optional(),
    customFields: activityWritableShape.customFields.optional(),
    links: z.array(activityLinkRefSchema).min(1).max(10),
    followUp: inlineFollowUpInputSchema.optional(),
  })
  .strict();
export type CreateActivityInput = z.input<typeof createActivityInputSchema>;

/**
 * PATCH /api/v1/activities/:id (perm `messages:write`) → Activity. Merge patch.
 * Only the author may edit; anyone with `org:manage` may edit or remove any entry.
 */
export const updateActivityInputSchema = z
  .object({ ...activityWritableShape, links: z.array(activityLinkRefSchema).min(1).max(10) })
  .partial()
  .strict();
export type UpdateActivityInput = z.input<typeof updateActivityInputSchema>;

/** POST /api/v1/activities/:id/links (perm `messages:write`) → Activity. Idempotent. */
export const addActivityLinkInputSchema = activityLinkRefSchema.strict();
export type AddActivityLinkInput = z.input<typeof addActivityLinkInputSchema>;

/**
 * GET /api/v1/activities?entityType=&entityId=&… → CursorPage<Activity>, newest first.
 * Read access to the named record is required; an unreadable one answers 404.
 */
export const listActivitiesQuerySchema = cursorQuerySchema.extend({
  entityType: activityLinkTypeSchema,
  entityId: uuidSchema,
  types: csvArraySchema(activityTypeSchema, ACTIVITY_TYPES.length).optional(),
  actorId: uuidSchema.optional(),
  from: timestampSchema.optional(),
  to: timestampSchema.optional(),
  pinnedOnly: z.enum(["true", "false"]).optional(),
  q: z.string().trim().max(200).optional(),
});
export type ListActivitiesQuery = z.infer<typeof listActivitiesQuerySchema>;

// ---------------------------------------------------------------------------
// Unified timeline (US-6.3)
// ---------------------------------------------------------------------------

/** A system milestone, projected from the audit trail. Rendered visually distinct. */
export const timelineMilestoneSchema = z.object({
  id: uuidSchema,
  action: z.string(),
  entityType: anyEntityTypeSchema,
  entityId: uuidSchema.nullable(),
  summary: z.string(),
  actor: actorSchema,
  occurredAt: timestampSchema,
});
export type TimelineMilestone = z.infer<typeof timelineMilestoneSchema>;

/** Exactly one of `activity` / `milestone` is non-null, per `kind`. */
export const timelineItemSchema = z.object({
  kind: z.enum(["activity", "milestone"]),
  id: uuidSchema,
  occurredAt: timestampSchema,
  /** Carried over from a lead at conversion — the UI badges it "Pre-sale" (PRD C-6). */
  isPreSale: z.boolean(),
  isPinned: z.boolean(),
  activity: activitySchema.nullable(),
  milestone: timelineMilestoneSchema.nullable(),
});
export type TimelineItem = z.infer<typeof timelineItemSchema>;

/** `pinned` is returned on the first page only (US-6.3: pinned notes stay on top). */
export const timelinePageSchema = z.object({
  items: z.array(timelineItemSchema),
  pinned: z.array(timelineItemSchema),
  nextCursor: z.string().nullable(),
  /** The records whose activities were merged into this feed (client + company + contacts). */
  sources: z.array(z.object({ entityType: activityLinkTypeSchema, entityId: uuidSchema })),
});
export type TimelinePage = z.infer<typeof timelinePageSchema>;

/**
 * How a page of the timeline is cut.
 *  · `chronological` (the default) — `limit` counts every entry, activities and
 *    system milestones alike, newest first.
 *  · `conversation` — `limit` counts only what PEOPLE did (calls, meetings,
 *    notes, email, WhatsApp). The milestones that happened between those
 *    entries still come back, in order, for the UI to fold into collapsible
 *    runs — so a burst of system events can never push the conversation off the
 *    first page (the Phase 1 QA finding).
 */
export const TIMELINE_LAYOUTS = ["chronological", "conversation"] as const;
export type TimelineLayout = (typeof TIMELINE_LAYOUTS)[number];

/** GET /api/v1/timeline/:entityType/:entityId — the unified reverse-chronological feed. */
export const timelineParamsSchema = z.object({ entityType: activityLinkTypeSchema, entityId: uuidSchema });
export const timelineQuerySchema = cursorQuerySchema.extend({
  types: csvArraySchema(activityTypeSchema, ACTIVITY_TYPES.length).optional(),
  actorId: uuidSchema.optional(),
  includeMilestones: z.enum(["true", "false"]).optional(),
  layout: z.enum(TIMELINE_LAYOUTS).optional(),
});
export type TimelineQuery = z.infer<typeof timelineQuerySchema>;

// ---------------------------------------------------------------------------
// Follow-ups (US-6.2)
// ---------------------------------------------------------------------------

export const followUpSchema = z.object({
  id: uuidSchema,
  entityType: activityLinkTypeSchema,
  entityId: uuidSchema,
  title: z.string(),
  note: z.string().nullable(),
  assignedTo: userRefSchema.nullable(),
  dueAt: timestampSchema,
  reminderAt: timestampSchema.nullable(),
  status: followUpStatusSchema,
  /** Computed server-side in the workspace timezone. */
  bucket: followUpBucketSchema,
  completedAt: timestampSchema.nullable(),
  completedBy: userRefSchema.nullable(),
  /** The activity logged when it was completed, if the user logged the outcome. */
  outcomeActivityId: uuidSchema.nullable(),
  createdBy: userRefSchema.nullable(),
  ...timestampsShape,
});
export type FollowUpDto = z.infer<typeof followUpSchema>;

const followUpWritableShape = {
  title: z.string().trim().min(1).max(300),
  note: z.string().trim().max(2000).nullable(),
  assignedToUserId: uuidSchema,
  dueAt: timestampSchema,
  reminderAt: timestampSchema.nullable(),
};

/** POST /api/v1/follow-ups (perm `tasks:write`) → 201 FollowUp. */
export const createFollowUpInputSchema = z
  .object({
    ...followUpWritableShape,
    note: followUpWritableShape.note.optional(),
    assignedToUserId: followUpWritableShape.assignedToUserId.optional(),
    reminderAt: followUpWritableShape.reminderAt.optional(),
    entityType: activityLinkTypeSchema,
    entityId: uuidSchema,
  })
  .strict();
export type CreateFollowUpInput = z.input<typeof createFollowUpInputSchema>;

/** PATCH /api/v1/follow-ups/:id (perm `tasks:write`) → FollowUp. Merge patch. */
export const updateFollowUpInputSchema = z.object(followUpWritableShape).partial().strict();
export type UpdateFollowUpInput = z.input<typeof updateFollowUpInputSchema>;

/**
 * POST /api/v1/follow-ups/:id/complete (perm `tasks:write`) → FollowUp.
 * `logOutcome` answers the "Log the outcome?" prompt of US-6.2 in the same
 * transaction, so the note can never be lost between two requests.
 */
export const completeFollowUpInputSchema = z
  .object({
    logOutcome: z
      .object({
        type: loggableActivityTypeSchema,
        body: z.string().trim().max(20_000).nullable().optional(),
        subject: z.string().trim().max(300).nullable().optional(),
        outcome: callOutcomeSchema.nullable().optional(),
        durationMinutes: z.number().int().min(0).max(24 * 60).nullable().optional(),
        visibility: activityVisibilitySchema.optional(),
      })
      .strict()
      .optional(),
    /** Chain the next step straight away — the habit US-6.2 is trying to build. */
    nextFollowUp: inlineFollowUpInputSchema.optional(),
  })
  .strict();
export type CompleteFollowUpInput = z.input<typeof completeFollowUpInputSchema>;

/** GET /api/v1/follow-ups → CursorPage<FollowUp>, soonest due first. */
export const listFollowUpsQuerySchema = cursorQuerySchema.extend({
  entityType: activityLinkTypeSchema.optional(),
  entityId: uuidSchema.optional(),
  assignedToUserId: uuidSchema.optional(),
  status: followUpStatusSchema.optional(),
  dueBefore: timestampSchema.optional(),
});
export type ListFollowUpsQuery = z.infer<typeof listFollowUpsQuerySchema>;

/** GET /api/v1/follow-ups/mine → the three buckets of US-6.2, each newest-due first. */
export const myFollowUpsSchema = z.object({
  overdue: z.array(followUpSchema),
  today: z.array(followUpSchema),
  upcoming: z.array(followUpSchema),
  counts: z.object({ overdue: z.number().int(), today: z.number().int(), upcoming: z.number().int() }),
});
export type MyFollowUps = z.infer<typeof myFollowUpsSchema>;

export const myFollowUpsQuerySchema = z.object({
  /** How far ahead "upcoming" reaches. */
  upcomingDays: z.coerce.number().int().min(1).max(90).default(14),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type MyFollowUpsQuery = z.infer<typeof myFollowUpsQuerySchema>;

// ---------------------------------------------------------------------------
// Message templates (US-6.4, US-6.6)
// ---------------------------------------------------------------------------

export const messageTemplateSchema = z.object({
  id: uuidSchema,
  channel: messageTemplateChannelSchema,
  name: z.string(),
  description: z.string().nullable(),
  /** Email only. */
  subject: z.string().nullable(),
  body: z.string(),
  /** Derived from the body + subject, so the editor can list what it will need. */
  mergeFields: z.array(z.string()),
  createdBy: userRefSchema.nullable(),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type MessageTemplateDto = z.infer<typeof messageTemplateSchema>;

const messageTemplateWritableShape = {
  channel: messageTemplateChannelSchema,
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).nullable(),
  subject: z.string().trim().max(300).nullable(),
  body: z.string().trim().min(1).max(10_000),
};

/** POST /api/v1/message-templates (perm `templates:manage`) → 201. Duplicate name per channel → CONFLICT. */
export const createMessageTemplateInputSchema = z
  .object({
    ...messageTemplateWritableShape,
    description: messageTemplateWritableShape.description.optional(),
    subject: messageTemplateWritableShape.subject.optional(),
  })
  .strict();
export type CreateMessageTemplateInput = z.input<typeof createMessageTemplateInputSchema>;

/** PATCH /api/v1/message-templates/:id (perm `templates:manage`) → MessageTemplate. Merge patch. */
export const updateMessageTemplateInputSchema = z.object(messageTemplateWritableShape).partial().strict();
export type UpdateMessageTemplateInput = z.input<typeof updateMessageTemplateInputSchema>;

export const listMessageTemplatesQuerySchema = z.object({
  channel: messageTemplateChannelSchema.optional(),
  q: z.string().trim().max(200).optional(),
});
export type ListMessageTemplatesQuery = z.infer<typeof listMessageTemplatesQuerySchema>;

/** POST /api/v1/message-templates/:id/preview → the rendered message for a real record. */
export const previewMessageTemplateInputSchema = z
  .object({
    contactId: uuidSchema.optional(),
    clientId: uuidSchema.optional(),
    companyId: uuidSchema.optional(),
    leadId: uuidSchema.optional(),
    /** Overrides for anything the server cannot resolve. */
    variables: z.record(z.string(), z.string().max(500)).optional(),
  })
  .strict();
export type PreviewMessageTemplateInput = z.input<typeof previewMessageTemplateInputSchema>;

export const messagePreviewSchema = z.object({
  subject: z.string().nullable(),
  body: z.string(),
  missingMergeFields: z.array(z.string()),
  /** Every token the server could resolve, so the web can show the substitution. */
  resolved: z.record(z.string(), z.string()),
});
export type MessagePreview = z.infer<typeof messagePreviewSchema>;

// ---------------------------------------------------------------------------
// Click-to-WhatsApp (US-6.4, PRD §5.5)
// ---------------------------------------------------------------------------

/**
 * POST /api/v1/activities/whatsapp-link (perm `messages:write`) → the wa.me URL
 * the browser opens, plus the activity that was logged for it. Phase 1 has no
 * Business API: we hand the user a click-to-chat link and record that we did.
 */
export const whatsappLinkInputSchema = z
  .object({
    contactId: uuidSchema.optional(),
    /** Used when the number is not on a contact record (or there is no contact yet). */
    phone: z.string().trim().min(6).max(24).optional(),
    templateId: uuidSchema.optional(),
    /** A one-off message instead of a template. */
    body: z.string().trim().min(1).max(4000).optional(),
    variables: z.record(z.string(), z.string().max(500)).optional(),
    links: z.array(activityLinkRefSchema).max(10).optional(),
    visibility: activityVisibilitySchema.optional(),
    occurredAt: timestampSchema.optional(),
  })
  .strict()
  .refine((o) => Boolean(o.contactId ?? o.phone), { message: "Give a contact or a phone number", path: ["contactId"] })
  .refine((o) => Boolean(o.templateId ?? o.body), { message: "Give a template or a message body", path: ["body"] })
  .refine((o) => Boolean(o.contactId) || (o.links?.length ?? 0) > 0, { message: "Link the message to at least one record", path: ["links"] });
export type WhatsappLinkInput = z.input<typeof whatsappLinkInputSchema>;

export const whatsappLinkResultSchema = z.object({
  url: z.string(),
  phone: z.string(),
  message: z.string(),
  missingMergeFields: z.array(z.string()),
  activity: activitySchema,
});
export type WhatsappLinkResult = z.infer<typeof whatsappLinkResultSchema>;

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/**
 * Which bucket a follow-up belongs in. `now` and `dueAt` are compared on the
 * workspace's calendar day, which the caller passes as `dayEndsAt` (the end of
 * "today" in the org timezone) — the server computes it once per request.
 */
export function followUpBucket(status: FollowUpStatus, dueAt: Date, now: Date, dayEndsAt: Date): FollowUpBucket {
  if (status !== "open") return "done";
  if (dueAt.getTime() < now.getTime()) return "overdue";
  if (dueAt.getTime() <= dayEndsAt.getTime()) return "today";
  return "upcoming";
}

/** Audit actions the timeline promotes to a visible milestone (US-6.3). */
export const MILESTONE_ACTION_SUFFIXES = [
  "created",
  "status_changed",
  "stage_changed",
  "converted",
  "won",
  "lost",
  "reactivated",
  "issued",
  "sent",
  "paid",
  "partially_paid",
  "payment_recorded",
  "refunded",
  "overdue",
  "accepted",
  "declined",
  "signed",
  "completed",
  "archived",
  "restored",
] as const;

export function isMilestoneAction(action: string): boolean {
  const verb = action.split(".").slice(1).join(".");
  return (MILESTONE_ACTION_SUFFIXES as readonly string[]).includes(verb);
}
